/** The KPI band at the top of the dashboard: four headline numbers plus a
 *  composition bar showing how the estate splits across states.
 *
 *  Every tile is a door (dashboard design rules §4.1): each links to the
 *  section of the board that explains its number. */

import { Card, SegmentBar, Statistic, Typography } from "@norican/react";
import type { ReactNode } from "react";

import type { MonitoringSummary } from "@/lib/api";

import {
  formatMs,
  formatPct,
  type SiteState,
  STATE_ORDER,
  STATUS_COLOR,
  STATUS_LABEL,
} from "./status";

interface EstateSummaryProps {
  summary: MonitoringSummary;
}

/** A KPI tile wrapped as an in-page link into the section it summarises. */
function TileLink({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  return (
    <a href={href} aria-label={label} className="block">
      {children}
    </a>
  );
}

export function EstateSummary({ summary }: EstateSummaryProps) {
  const attention = summary.down + summary.degraded;
  const allWell = attention === 0 && summary.unknown === 0;

  // Only show states that actually occur — an empty segment would just be noise.
  const counts: Record<SiteState, number> = {
    up: summary.up,
    degraded: summary.degraded,
    down: summary.down,
    unknown: summary.unknown,
  };
  const segments = STATE_ORDER.filter((state) => counts[state] > 0).map((state) => ({
    value: counts[state],
    label: `${STATUS_LABEL[state]} (${counts[state]})`,
    color: STATUS_COLOR[state],
  }));

  // The uptime figure only covers what this process has observed, so say so
  // rather than implying a 30-day SLA.
  const windowCaption =
    summary.window_started_at == null
      ? "No checks recorded yet"
      : `${summary.total} sites · since ${new Date(summary.window_started_at).toLocaleTimeString(
          [],
          {
            hour: "2-digit",
            minute: "2-digit",
          },
        )}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <TileLink href="#sites" label="Jump to the site list">
          <Card variant="outline" accent="left" accentTone={allWell ? "success" : "danger"}>
            <Card.Body>
              <Statistic
                label="Sites online"
                value={`${summary.up} / ${summary.total}`}
                tone={allWell ? "success" : "danger"}
                size="lg"
                caption={allWell ? "All systems operational" : "Estate not fully healthy"}
              />
            </Card.Body>
          </Card>
        </TileLink>

        <TileLink href="#latency" label="Jump to the response time trend">
          <Card variant="outline" accent="left" accentTone="accent">
            <Card.Body>
              <Statistic
                label="Average response"
                value={formatMs(summary.avg_response_time_ms)}
                size="lg"
                caption="Mean across all monitored sites"
              />
            </Card.Body>
          </Card>
        </TileLink>

        <TileLink href="#history" label="Jump to the availability history">
          <Card variant="outline" accent="left" accentTone="accent">
            <Card.Body>
              <Statistic
                label="Availability"
                value={formatPct(summary.uptime_pct)}
                size="lg"
                progress={summary.uptime_pct ?? 0}
                progressTone={
                  (summary.uptime_pct ?? 100) >= 99
                    ? "success"
                    : (summary.uptime_pct ?? 100) >= 95
                      ? "warning"
                      : "danger"
                }
                caption={windowCaption}
              />
            </Card.Body>
          </Card>
        </TileLink>

        <TileLink href="#sites" label="Jump to the site list">
          <Card variant="outline" accent="left" accentTone={attention > 0 ? "warning" : "success"}>
            <Card.Body>
              <Statistic
                label="Needs attention"
                value={String(attention)}
                tone={attention > 0 ? "warning" : "success"}
                size="lg"
                caption={
                  attention > 0
                    ? `${summary.down} down · ${summary.degraded} degraded`
                    : "Nothing flagged"
                }
              />
            </Card.Body>
          </Card>
        </TileLink>
      </div>

      {segments.length > 0 && (
        <Card variant="subtle">
          <Card.Body>
            <Typography
              variant="caption"
              as="p"
              className="mb-3 font-semibold tracking-wide uppercase"
            >
              Estate composition
            </Typography>
            <SegmentBar
              segments={segments}
              size="lg"
              showLegend
              aria-label="Monitored sites by status"
            />
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
